import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Rocket, RefreshCw, CheckCircle2, AlertTriangle, Database, Image as ImageIcon, Trash2, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SNAPSHOT_META = {
  content: { title: 'Site Content', desc: 'Brands, services, programs, events, webinars, contracts & CMS.' },
  studio: { title: 'Social Studio', desc: 'Brand profiles, references & 90-day content plans (curated).' },
  flyer_intel: { title: 'Flyer Studio + Intelligence', desc: 'Flyer brands/themes/references, saved intelligence + embedded media.' },
};

function SnapshotCard({ id, snap }) {
  const meta = SNAPSHOT_META[id] || { title: id, desc: '' };
  const colls = snap?.collections || {};
  const totalRows = Object.values(colls).reduce((a, b) => a + b, 0);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`deploy-snap-${id}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <Database size={18} className="text-[#1D4ED8]" />
          <h3 className="font-bold text-[#0A0F1C]">{meta.title}</h3>
        </div>
        {snap?.exists
          ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid={`deploy-snap-${id}-ok`}>Captured</span>
          : <span className="text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full" data-testid={`deploy-snap-${id}-missing`}>Missing</span>}
      </div>
      <p className="text-xs text-[#94A3B8] mb-3">{meta.desc}</p>
      {snap?.exists && (
        <>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#475569] mb-3">
            <span><b className="text-[#0A0F1C]">{totalRows}</b> rows</span>
            {snap.media_count != null && (
              <span className="inline-flex items-center gap-1"><ImageIcon size={13} /><b className="text-[#0A0F1C]">{snap.media_count}</b> media</span>
            )}
            <span>{snap.size_mb} MB</span>
          </div>
          <div className="border-t border-[#F1F5F9] pt-2 max-h-44 overflow-auto">
            {Object.entries(colls).filter(([, v]) => v > 0).map(([c, v]) => (
              <div key={c} className="flex justify-between py-0.5 text-xs">
                <span className="text-[#64748B]">{c.replace(/_/g, ' ')}</span>
                <span className="font-semibold text-[#0A0F1C]">{v}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminDeployReadiness() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [syncedAt, setSyncedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/deploy/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load deploy status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/deploy/refresh`, {}, { withCredentials: true });
      if (r.data.ok) { toast.success('Preview config captured — ready to deploy'); setSyncedAt(new Date()); }
      else toast.error('Some snapshots failed to refresh');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally { setRefreshing(false); }
  };

  const cleanup = async () => {
    if (!window.confirm('Permanently delete leftover TEST_* rows from the live database?')) return;
    setCleaning(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/deploy/cleanup-test-data`, {}, { withCredentials: true });
      toast.success(`Removed ${r.data.total} test rows`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally { setCleaning(false); }
  };

  const ready = status?.ready;
  const testTotal = status?.test_data?.total || 0;

  return (
    <AdminLayout title="Deploy Readiness">
      <div data-testid="admin-deploy-page" className="max-w-4xl">
        <div className="flex items-center gap-3 mb-2">
          <Rocket className="text-[#1D4ED8]" size={24} />
          <h1 className="heading-2 text-2xl text-[#0A0F1C]">Deploy Readiness</h1>
        </div>
        <p className="text-sm text-[#475569] mb-6 max-w-2xl">
          Your production <b>database is kept</b> across redeploys — bookings, payments, enrollments, leads and any
          payment links you create live are never wiped. What this page does is capture the <b>configuration you built in
          preview</b> (brands, services, programs, packages, CMS text, colors) so a redeploy carries it to production.
          Press <b>Sync</b> below before you deploy.
        </p>

        <div className="bg-gradient-to-r from-[#1D4ED8] to-[#2563EB] rounded-2xl p-5 mb-6 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="deploy-sync-hero">
          <div>
            <div className="flex items-center gap-2 font-bold text-lg"><Rocket size={18} /> Sync Preview → Production</div>
            <p className="text-sm text-blue-100 mt-1 max-w-md">
              One click captures everything you've built in preview. Then hit <b>Deploy</b> (top bar) to push it live.
            </p>
            {syncedAt && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-white/15 px-2.5 py-1 rounded-full" data-testid="deploy-synced-at">
                <CheckCircle2 size={13} /> Synced at {syncedAt.toLocaleTimeString()} — now click Deploy to go live
              </div>
            )}
          </div>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="shrink-0 inline-flex items-center gap-2 bg-white text-[#1D4ED8] px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-60 hover:bg-blue-50 transition-colors shadow-sm"
            data-testid="deploy-sync-btn"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#64748B] py-12" data-testid="deploy-loading">
            <Loader2 className="animate-spin" size={18} /> Checking deploy state…
          </div>
        ) : status && (
          <>
            <div
              className={`rounded-xl p-4 mb-6 flex items-center gap-3 border ${ready ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
              data-testid="deploy-readiness-banner"
            >
              {ready
                ? <CheckCircle2 className="text-emerald-600 shrink-0" size={22} />
                : <AlertTriangle className="text-amber-600 shrink-0" size={22} />}
              <div className="text-sm">
                <div className={`font-bold ${ready ? 'text-emerald-800' : 'text-amber-800'}`} data-testid="deploy-readiness-state">
                  {ready ? 'Ready to deploy' : 'Action needed before deploy'}
                </div>
                <div className={ready ? 'text-emerald-700' : 'text-amber-700'}>
                  {ready
                    ? 'All snapshots captured and no leftover test data found.'
                    : testTotal > 0
                      ? `${testTotal} leftover TEST_* row(s) found in the live database — clean them before launch.`
                      : 'One or more snapshots are missing — run Refresh.'}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={refresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60 hover:bg-[#1E40AF] transition-colors"
                data-testid="deploy-refresh-btn"
              >
                <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
                {refreshing ? 'Refreshing snapshots…' : 'Refresh snapshots'}
              </button>
              {testTotal > 0 && (
                <button
                  onClick={cleanup}
                  disabled={cleaning}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60 hover:bg-red-700 transition-colors"
                  data-testid="deploy-cleanup-btn"
                >
                  <Trash2 size={15} />{cleaning ? 'Cleaning…' : `Clean ${testTotal} test row(s)`}
                </button>
              )}
              <button
                onClick={load}
                className="inline-flex items-center gap-2 border border-[#E2E8F0] text-[#475569] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#F8FAFC]"
                data-testid="deploy-recheck-btn"
              >
                Re-check
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {['content', 'studio', 'flyer_intel'].map((id) => (
                <SnapshotCard key={id} id={id} snap={status.snapshots?.[id]} />
              ))}
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="deploy-live-compare">
              <h3 className="font-bold text-[#0A0F1C] mb-3 text-sm">Live preview vs captured (Flyer / Intelligence)</h3>
              <div className="divide-y border-t border-[#F1F5F9]">
                {Object.entries(status.live_counts || {}).map(([c, live]) => {
                  const captured = status.snapshots?.flyer_intel?.collections?.[c] ?? 0;
                  const diff = live - captured;
                  return (
                    <div key={c} className="flex items-center justify-between py-1.5 text-sm" data-testid={`deploy-live-${c}`}>
                      <span className="text-[#475569]">{c.replace(/_/g, ' ')}</span>
                      <span className="text-[#0A0F1C]">
                        <b>{captured}</b> captured
                        <span className="text-[#94A3B8]"> / {live} live</span>
                        {diff > 0 && <span className="ml-2 text-amber-600 text-xs">({diff} excluded — test rows)</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
